import { z } from 'zod';

import { ThemeFontFamilies } from '../../../enums/font-families.enum';
import { ThemeFontTags } from '../../../enums/font-tags.enum';

/**
 * A font value as the pre-versioning theme stored it.
 *
 * Deliberately as permissive as the data: the host typed these through `Pick<JssStyle, …>`, whose
 * `JssValue` is `string | number | Array<…> | null | false`, so a cleared field is `null` and a
 * size is `'16px'` in some rows and `16` in others. This schema only has to RECOGNIZE what is out
 * there — `migrateWebsiteThemeFontLegacy` is what turns it into something typed.
 */
const legacyCssValueSchema = z.union([z.string(), z.number(), z.null(), z.boolean(), z.array(z.unknown())]);

/** One breakpoint's bag. Loose: a panel may have written CSS properties beyond these seven. */
export const websiteThemeFontStylesLegacySchema = z.looseObject({
    fontFamily: legacyCssValueSchema.optional(),
    fontSize: legacyCssValueSchema.optional(),
    fontWeight: legacyCssValueSchema.optional(),
    fontStyle: legacyCssValueSchema.optional(),
    lineHeight: legacyCssValueSchema.optional(),
    textTransform: legacyCssValueSchema.optional(),
    letterSpacing: legacyCssValueSchema.optional(),
    color: legacyCssValueSchema.optional(),
});

/**
 * One tag's font, breakpoint-first: three sibling bags, with `tabletStyles`/`mobileStyles` present
 * only when the user opened those tabs. v0 inverts this to property-first — see `themeFontSchema`.
 */
export const websiteThemeFontLegacySchema = z.object({
    tag: z.enum(ThemeFontTags),
    font: z.enum(ThemeFontFamilies),
    styles: websiteThemeFontStylesLegacySchema,
    tabletStyles: websiteThemeFontStylesLegacySchema.optional(),
    mobileStyles: websiteThemeFontStylesLegacySchema.optional(),
});

export type WebsiteThemeFontStylesLegacy = z.infer<typeof websiteThemeFontStylesLegacySchema>;
export type WebsiteThemeFontLegacy = z.infer<typeof websiteThemeFontLegacySchema>;
